import { PageRequest } from '@/core/request/PageRequest';
import { PurchaseFinancialInstallmentList } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/PurchaseDetailsPage/PurchaseFinancialPage';
import { Moment } from 'moment';
import { InstallmentTag } from '../InstallmentTags/InstallmentTags.type';

export enum Creditor {
  employee = 'domain.enums.creditor_type.employee',
  supplier = 'domain.enums.creditor_type.supplier',
}

export enum InstallmentSituation {
  open = 'domain.enums.situation_installment_payable.open',
  canceled = 'domain.enums.situation_installment_payable.canceled',
  settled = 'domain.enums.situation_installment_payable.settled',
  grouped = 'domain.enums.situation_installment_payable.grouped',
}

export const InstallmentSituationType = {
  [InstallmentSituation.open]: 'Aberta',
  [InstallmentSituation.canceled]: 'Cancelado',
  [InstallmentSituation.settled]: 'Liquidada',
  [InstallmentSituation.grouped]: 'Agrupado',
};

export const AccountPayableSituationColor = {
  [InstallmentSituation.open]: 'blue',
  [InstallmentSituation.settled]: 'green',
  [InstallmentSituation.grouped]: 'yellow',
  [InstallmentSituation.canceled]: 'red',
};

export enum AccountPayableOrderColumn {
  code = 'domain.enums.order_columns_account_payable.code',
  company = 'domain.enums.order_columns_account_payable.company',
  creditor = 'domain.enums.order_columns_account_payable.creditor',
  description = 'domain.enums.order_columns_account_payable.description',
  dueDate = 'domain.enums.order_columns_account_payable.due_date',
  emissionDate = 'domain.enums.order_columns_account_payable.emissionDate',
  payDate = 'domain.enums.order_columns_account_payable.payDate',
  value = 'domain.enums.order_columns_account_payable.value',
  documentType = 'domain.enums.order_columns_account_payable.document_type',
  installmentTag = 'domain.enums.order_columns_account_payable.installmentTag',
  authorizationNumber = 'domain.enums.order_columns_account_payable.authorization_number',
}

export enum AccountPayableFieldOption {
  code = 'domain.enums.order_columns_account_payable.code',
  company = 'domain.enums.order_columns_account_payable.company',
  creditor = 'domain.enums.order_columns_account_payable.creditor',
  description = 'domain.enums.order_columns_account_payable.description',
  dueDate = 'domain.enums.order_columns_account_payable.due_date',
  emissionDate = 'domain.enums.order_columns_account_payable.emission_date',
  payDate = 'domain.enums.order_columns_account_payable.pay_date',
  value = 'domain.enums.order_columns_account_payable.value',
  documentType = 'domain.enums.order_columns_account_payable.document_type',
  situation = 'domain.enums.order_columns_account_payable.situation',
  documentNumber = 'domain.enums.order_columns_account_payable.documentNumber',
  installmentTag = 'domain.enums.order_columns_account_payable.installmentTag',
  authorizationNumber = 'domain.enums.financial_field_options.authorization_number',
}

export const AccountPayableColumnConfig: Record<
  string,
  AccountPayableOrderColumn
> = {
  [AccountPayableFieldOption.code]: AccountPayableOrderColumn.code,
  [AccountPayableFieldOption.company]: AccountPayableOrderColumn.company,
  [AccountPayableFieldOption.creditor]: AccountPayableOrderColumn.creditor,
  [AccountPayableFieldOption.description]:
    AccountPayableOrderColumn.description,
  [AccountPayableFieldOption.dueDate]: AccountPayableOrderColumn.dueDate,
  [AccountPayableFieldOption.emissionDate]:
    AccountPayableOrderColumn.emissionDate,
  [AccountPayableFieldOption.payDate]: AccountPayableOrderColumn.payDate,
  [AccountPayableFieldOption.value]: AccountPayableOrderColumn.value,
  [AccountPayableFieldOption.documentType]:
    AccountPayableOrderColumn.documentType,
  [AccountPayableFieldOption.installmentTag]:
    AccountPayableOrderColumn.installmentTag,
  [AccountPayableFieldOption.authorizationNumber]:
    AccountPayableOrderColumn.authorizationNumber,
};

export interface AccountPayableReturnListInstallments {
  id: number;
  code: number;
  companyId: number;
  costCenterId: number;
  employeeId: number;
  supplierId: number;
  planAccountId: number;
  creditor: Creditor;
  isOriginalCommission: boolean;
  installmentPayables: Installment[];
  purchase: boolean;
}

export interface AccountPayable {
  id: number;
  code: number;
  companyId: number;
  costCenterId: number;
  employeeId: number;
  hasBond: boolean;
  supplierId: number;
  planAccountId: number;
  creditor: Creditor;
  installmentPayable: Installment;
}

export interface Installment {
  id: number;
  installmentNumber: number;
  accountPayableId: number;
  situation: InstallmentSituation;
  bankAccountId: number;
  conciliationDate: string;
  creationDate: string;
  description: string;
  discountIncrease: number;
  documentNumber: string;
  documentTypeId: number;
  emissionDate: string;
  dueDate: string;
  payDate: string;
  updateDate: string;
  installmentValue: number;
  observation: string;
  credit: number;
  conciliateInstallmentResponse?: ConciliateInstallmentResponse;
  installmentTag?: InstallmentTag;
}

export interface ConciliateInstallmentResponse {
  transactionName: string;
  transactionValue: number;
  transactionPayDate: string;
  conciliatedDate: string;
}

export interface InstallmentList {
  id: number;
  code: string;
  companyName: string;
  creditor: Creditor;
  description: string;
  documentTypeId: number;
  documentType: string;
  dueDate: string;
  payDate?: string;
  emissionDate?: string;
  situation: InstallmentSituation;
  installmentValue: number;
  discountIncrease: number;
  accountPayableId: number;
  bankAccountId: number;
  credit: number;
  isOriginalCommission: boolean;
  installmentTag: InstallmentTag;
  conciliateInstallmentResponse?: ConciliateInstallmentResponse;
}

export interface ListInstallmentsQuery extends PageRequest {
  search?: string;
  code?: string;
  companyId?: number;
  creditor?: string;
  description?: string;
  documentTypeId?: number;
  dueDate?: string;
  payDate?: string;
  emissionDate?: string;
  situation?: InstallmentSituation;
  installmentValue?: number;
  orderColumns?: AccountPayableOrderColumn;
  bankAccountId?: number;
  costCenterId?: number;
  documentNumber?: string;
  purchasingDocumentNumber?: string;
  purchaseCode?: string;
  cnpjCreditor?: string;
  initialDueDate?: string;
  finalDueDate?: string;
  initialPayDate?: string;
  finalPayDate?: string;
  initialEmissionDate?: string;
  finalEmissionDate?: string;
  observation?: string;
}

export interface AccountPayableReleaseRequest {
  companyId: number;
  employeeId: number;
  supplierId: number;
  planAccountId: number;
  costCenterId: number;
  description: string;
  documentNumber: string;
  installmentNumber: number;
  documentTypeId: number;
  emissionDate: string;
  dueDate?: string;
  bankAccountId: number;
  installmentValue: number;
  discountIncrease: number;
  creditor: Creditor;
}

export interface AccountPayableReleaseFormValues {
  situation: string;
  companyId: number;
  supplier: string;
  supplierId: number;
  employee: string;
  employeeId: number;
  planAccountId: number;
  costCenterId: number;
  description: string;
  documentNumber: string;
  installmentNumber: number;
  documentTypeId: number;
  emissionDate: Moment;
  dueDate: Moment;
  payDate?: Moment;
  bankAccountId: number;
  installmentValue: number;
  creditor: Creditor;
  discountIncrease: number;
  totalAmount: number;
  observation: string;
  credit: number;
}

export interface AccountPayableReleaseUpdateRequest {
  accountPayableId: number;
  installmentPayableId: number;
  employeeId: number;
  supplierId: number;
  planAccountId: number;
  costCenterId: number;
  description: string;
  documentNumber: string;
  documentTypeId: number;
  emissionDate: string;
  dueDate: string;
  bankAccountId: number;
  discountIncrease: number;
  installmentValue: number;
  creditor: Creditor;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentCancelRequest {
  id: number;
  installmentPayableId: number;
}

export interface SettleInstallmentSingleData {
  id?: number;
  installmentPayableId?: number;
  installmentValue?: number;
  bankAccountId?: number;
  documentTypeId?: number;
  credit?: number;
  payDate?: Moment;
  discountIncrease?: number;
  netValue?: number;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentSettleFormValues {
  bankAccountId: number;
  documentTypeId: number;
  payDate: Moment;
  dueDate: Moment;
  netValue: number;
  amountToBeSettled: number;
  discountIncrease?: number;
}

export interface InstallmentSettleSingleRequest {
  id: number;
  installmentPayableId: number;
  bankAccountId: number;
  documentTypeId: number;
  payDate: string;
  discountIncrease?: number;
  netValue: number;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentSettlePartiallyRequest {
  id: number;
  installmentPayableId: number;
  bankAccountId: number;
  documentTypeId: number;
  dueDate: string;
  netValue: number;
  amountToBeSettled: number;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentSettleMultipleRequest {
  installmentPayableId: number[];
  accountPayableIds: number[];
  bankAccountId: number;
  payDate: string;
  discountIncrease?: number;
}

export interface InstallmentSplitFormValues {
  valueTotal: number;
  fromThatDate: Moment;
  intervalDays: number;
  quantityInstallments: number;
}

export interface InstallmentSplitRequest {
  installmentPayableId: number;
  accountPayableId: number;
  valueTotal: number;
  fromThatDate: string;
  intervalDays: number;
  quantityInstallments: number;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentReverseFormValues {
  planAccountId: number;
  reversalDate: Moment;
}
export interface InstallmentReverseRequest {
  id: number;
  installmentPayableId: number;
  planAccountId: number;
  reversalDate: string;
  supplierId: number;
}
export interface InstallmentRenegotiateFormValues {
  valueTotal: number;
  fromThatDate: Moment;
  intervalDays: number;
  quantityInstallments: number;
}

export interface InstallmentRenegotiateRequest {
  installmentPayableId: number;
  accountId: number;
  valueTotal: number;
  fromThatDate: string;
  intervalDays: number;
  quantityInstallments: number;
  currentSituation?: InstallmentSituation;
}

export interface InstallmentRenegotiateResponse {
  accountId: number;
  installmentId: number;
}

export interface AccountPayableGroupingFormValues {
  companyId: number;
  creditor: Creditor;
  employeeId: number;
  employee: string;
  supplierId: number;
  supplier: string;
}

export interface AccountPayableGroupingRequest extends PageRequest {
  companyId?: number;
  creditor?: Creditor;
  employeeId?: number;
  supplierId?: number;
  supplier?: string;
}

export interface AccountPayableGroupingResponse {
  accountId: number;
  code: number;
  description: string;
  installmentNumber: number;
  totalValue: number;
}

export interface AccountPayableGenerateGroupingFormValues {
  costCenterId: number;
  planAccountId: number;
  installmentsFirstDueDate: Moment;
  quantityInstallments: number;
  intervalDays: number;
  totalValue: number;
  accountIds: number[];
}

export interface InstallmentFromGroupingsList {
  description: string;
  documentNumber?: string;
  documentTypeId?: number;
  bankAccountId?: number;
  installmentValue: number;
  dueDate: Moment;
}

export interface AccountPayableGenerateGroupingRequest {
  costCenterId: number;
  planAccountId: number;
  installmentsFirstDueDate: string;
  quantityInstallments: number;
  intervalDays: number;
  totalValue: number;
  accountIds: number[];
  installmentFromGroupings: (Omit<InstallmentFromGroupingsList, 'dueDate'> & {
    dueDate: string;
  })[];
}

export interface AccountPayableRangeDateRequest {
  initialDate: string;
  finalDate: string;
  companyId?: number;
}

export interface AccountPayableRangeDateResponse {
  id: number;
  installmentNumber: number;
  accountPayableId: number;
  accountPayableEmployeeId: number;
  accountPayableEmployeeName: string;
  accountPayableSupplierId: number;
  accountPayableSupplierName: string;
  accountPayableCode: number;
  situation: InstallmentSituation;
  bankAccountId: number;
  conciliationDate: string;
  creationDate: string;
  description: string;
  discountIncrease?: number;
  documentNumber: string;
  documentTypeId: number;
  emissionDate: string;
  dueDate: string;
  payDate: string;
  updateDate: string;
  installmentValue: number;
  observation: string;
}

export interface GenerateFinancialByPurchase {
  purchaseId: number;
  companyId: number;
  costCenterId: number;
  planAccountId: number;
  supplierId: number;
  installmentPayablePurchases: (Omit<
    PurchaseFinancialInstallmentList,
    'dueDate'
  > & {
    dueDate: string;
  })[];
}

export interface AccountPayableListByPurchase extends PageRequest {
  purchaseId: number;
}

export interface AccountPayableReverseMultipleInstallmentRequest {
  installmentPayableId: React.Key[];
  planAccountId: number;
  reversalDate: string;
}

export interface AccountPayableDeleteMultipleInstallmentRequest {
  installmentIds: React.Key[];
}

export interface AccountPayableListGroupingResponse {
  id: number;
  code: string;
  codeInt: number;
  installmentInt: number;
  companyName: string;
  creditor: string;
  creditorCnpj: string;
  description: string;
  documentTypeId: number;
  bankAccountId: number;
  documentType?: string;
  dueDate: string;
  situation: InstallmentSituation;
  installmentValue: number;
  credit: number;
  discountIncrease: number;
  accountPayableId: number;
}

export interface AccountPayableArchive {
  id: number;
  fileName: string;
  filePath: string;
}

export interface AccountPayableArchiveCreateRequest {
  accountPayableId: number;
  files: File[];
}

export interface AccountPayableArchiveRemoveRequest {
  accountPayableId: number;
  fileId: number;
}

export interface AccountPayableIncludeTagRequest {
  accountPayableId: number;
  installmentPayableId: number;
  installmentTagId?: number | null;
}
